import { ModalRef, useTranslation } from '@msdk/common'
import {
  ButtonIcon,
  ButtonPrimary,
  Card,
  CloseIcon,
  COLORS,
  FormFooterWrapper,
  FormHeaderWrapper,
  FormWrapper,
  styled,
  Typography,
} from '@msdk/ui'
import React, { useState } from 'react'

import { FieldEntityPair } from '../field-entity-pair'
import { NodeGroup, NodeItem } from '../node'
import { Group } from './Group'
import { Item } from './Item'

const StyledCard = styled(Card)`
  max-height: 80vh;
  overflow: auto;
`

const StyledFormHeaderWrapper = styled(FormHeaderWrapper)`
  padding: 16px 24px;
  gap: 32px;
`

const StyledFormWrapper = styled(FormWrapper)`
  padding: 24px 24px 16px;
  border-top: 1px solid ${COLORS.divider};
  border-bottom: 1px solid ${COLORS.divider};
`

interface Props {
  nodes: NodeItem[]
  selectedItems: FieldEntityPair[]
  idml: string
  description: string
  multiselect: boolean
  modalRef?: ModalRef
}

export function SelectorPanel(props: Props) {
  const t = useTranslation()
  const [selected, setSelected] = useState<FieldEntityPair[]>(props.selectedItems)

  const onItemToggle = (item: FieldEntityPair) => {
    if (props.multiselect) {
      const idx = selected.findIndex((i) => i.entityName === item.entityName && i.fieldName === item.fieldName)
      const cloned = [...selected]
      if (idx !== -1) {
        cloned.splice(idx, 1)
      } else {
        cloned.push(item)
      }
      setSelected(cloned)
    } else {
      const exists = !!selected.find((i) => i.equals(item))
      setSelected(exists ? [] : [item])
    }
  }

  const onToggleAll = (items: FieldEntityPair[]) => {
    const cloned = [...selected]
    const areSomeSelected = someSelected(items)
    const areAllSelected = allSelected(items)
    const shouldSelect = areSomeSelected || (!areSomeSelected && !areAllSelected)
    if (shouldSelect) {
      const toSelect = items.filter((i) => cloned.findIndex((s) => i.equals(s)) === -1)
      setSelected([...cloned, ...toSelect])
    } else {
      const updated = cloned.filter((s) => items.findIndex((i) => s.equals(i)) === -1)
      setSelected(updated)
    }
  }

  const onConfirm = () => {
    props.modalRef?.close(selected)
  }

  const allSelected = (items: FieldEntityPair[]) => {
    let res = true && selected.length > 0
    res &&= items.some((p) => selected.find((c) => c.equals(p)))
    return res
  }

  const someSelected = (items: FieldEntityPair[]) => {
    let res = false
    selected.forEach((c) => {
      res ||= items.some((p) => c.equals(p))
    })
    return res && !allSelected(items)
  }

  const onClose = () => {
    props.modalRef?.close()
  }

  return (
    <StyledCard>
      <StyledFormHeaderWrapper>
        <Typography variant="h6">{t(props.idml, props.description)}</Typography>
        <ButtonIcon onClick={onClose}>
          <CloseIcon />
        </ButtonIcon>
      </StyledFormHeaderWrapper>
      <StyledFormWrapper>
        {props.nodes.map((n) => {
          if (n instanceof NodeGroup)
            return (
              <Group
                item={n}
                key={n.value}
                selected={selected}
                onToggle={onItemToggle}
                onToggleAll={onToggleAll}
                multiselect={props.multiselect}
              />
            )
          else return <Item item={n} key={n.value} selected={selected} onToggle={onItemToggle} />
        })}
      </StyledFormWrapper>
      <FormFooterWrapper>
        <ButtonPrimary onClick={onConfirm}>{t('CMPS_VIEW_MULTI_SELECT_CONFIRM', 'Confirm Selection')}</ButtonPrimary>
      </FormFooterWrapper>
    </StyledCard>
  )
}
